<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-02-26 (0.1.0) (jhoward)
 *   - Replace getOffers import with offers from @rss/routes/account.
 *   - Import and use TournamentOffer, InfoOffer, and ConcertOffer components.
 *   - Update offer rendering logic to use new offer components based on offer category.
 *   - Refactor fetchOffers to use new route definition.
 *   - Remove inline offer rendering in favor of component-based approach.
 *
 * 2026-02-28 (0.2.0) (jhoward)
 *   - Remove manual universalId input and fetching logic.
 *   - Use appStore.getAccountOffers to load offers on mount.
 *
 * 2026-03-02 (0.2.1) (jhoward)
 *   - Refactor home view to use PeppermillViewport and content blocks for offers display.
 *
 * 2026-03-11 (0.3.0) (jhoward)
 *   - Add new ViewOffers.vue with offer filtering, category buttons, and offer components.
 *   - Use getAccountOffers helper and appStore for offers data.
 *   - Add scoped styles for offer filter UI.
 *
 * 2026-03-13 (0.4.0) (jhoward)
 *   - Use Offer component for all offers.
 *   - Add fade transition for offer list.
 *
 * 2026-03-17 (0.4.1) (jhoward)
 *   - Update filterByCategory to allow 'All Offers' button to clear filters.
 *   - Add horizontal scroll and white-space styling for filter buttons.
 *
 * 2026-03-24 (0.5.0) (jhoward)
 *   - Add back-to-home router-link above offers list.
 *   - Improve styling for back button and filter bar.
 *
 * 2026-04-09 (0.6.0) (jhoward)
 *   - Add PIN-required and error loading states to offer view.
 *   - Add categoryTitleOverrides for custom category labels.
 *   - Update offer rendering to use transition-group 'list' and index in key.
 *   - Add support for linked account offers in UI.
 *   - Update CSS for new layout and animation.
 *
 * 2026-09-08 (0.7.0) (jhoward)
 *   - Add a 'Redeemed' filter alongside the category buttons, with a count in the same data-offer-count slot the
 *     category buttons use. It is a separate axis rather than another category, so it composes with whichever
 *     category is selected instead of replacing it.
 *
 * 2026-09-09 (0.8.0) (jhoward)
 *   - Add an 'Expiring Soon' filter matching the urgencies the expiry badge calls out, and a sort control
 *     (expiring soonest / newest / property). sortDate was on OpcOffer and unused; the grid rendered in raw API order.
 *   - Render only the offers that pass the filters instead of hiding the rest with v-show, so the transition-group
 *     animates in and out, and key on the offer identity rather than the loop index so a reorder does not reuse the
 *     wrong card.
 *   - Add an empty state for when the selected filters match nothing.
 *
 * 2026-09-18 (0.8.1) (jhoward)
 *   - Say offers are loading while they load. The states keyed off the global isLoaderActive flag, which is still
 *     false on the first paint because onMounted runs after it, so the view opened on 'Error Loading Offers' and then
 *     on the filter bar's 'No offers match the filters you have selected.' before a single offer had arrived.
 *
 * 2026-09-24 (0.9.0) (jhoward)
 *   - Label every category button through getOfferCategoryLabel(), in place of categoryTitleOverrides, which had
 *     BJTOURN alone; every other button showed its stored code, TOURN or ENT. BJTOURN now reads 'Blackjack Tournament',
 *     as the admin labels it, rather than 'Blackjack'.
 *   - Order the buttons by label rather than by code.
 *   - Fix the count on an uncategorized offer's button, which was stored under 'Uncategorized' and looked up under ''.
 *
 * 2026-10-09 (0.10.0) (jhoward)
 *   - Wrap the filter buttons onto further rows instead of scrolling them sideways. overflow-x: auto also made the bar
 *     scroll vertically, so it drew a scrollbar, and an M3 filter chip row wraps. A gap replaces the sibling margin.
 *
 * 2026-10-10 (0.11.0) (jhoward)
 *   - The page is now kept alive, so it no longer refetches the offers on every visit. useRevalidateOnActivate() refreshes
 *     them silently when the patron returns after five minutes, or comes back to the tab after that long.
 *
 * 2026-11-02 (0.12.0) (jhoward)
 *   - Hold the order of the offers until the sort or the set of offers changes. Redeeming or cancelling an offer
 *     re-sorted the list, and under 'expiring soonest' a redeemed offer (no expiry) jumped to the bottom. Filters still
 *     apply live. The card's key is now built by getOfferKey(), shared with the ordering.
 *   - 'Expiring soonest' sorts on OFFER_APPLICABLE_END_TS alone, redeemed or not. It went through getOfferExpiry(),
 *     which reports no expiry for a redeemed offer, so redeemed offers were pushed after every other offer.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import MaterialButton from '@framework/components/material/MaterialButton.vue';
import type { SelectOption } from '@framework/components/material/MaterialFormSelect.vue';
import MaterialFormSelect from '@framework/components/material/MaterialFormSelect.vue';
import { computed, onMounted, ref, watch } from 'vue';
import PeppermillViewport from '@/components/framework-proxy/PeppermillViewport.vue';
import Offer from '@/components/offers/Offer.vue';
import { getOfferCategoryLabel } from '@/defs/api/OfferCopyCategory';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import getAccountOffers from '@/helpers/getAccountOffers';
import { getDateFromEngage } from '@/helpers/getdateFromEngage';
import getOfferExpiry from '@/helpers/getOfferExpiry';
import getPropertyName from '@/helpers/getPropertyName';
import isOfferRedeemed from '@/helpers/isOfferRedeemed';
import useRevalidateOnActivate from '@/helpers/useRevalidateOnActivate';
import useAppStore from '@/store';

type OfferSortMode = 'expiring' | 'newest' | 'property';

const appStore = useAppStore();
//onMounted runs after the first paint, so the view has to open in the loading state rather than wait for the fetch to
//raise the store flag - otherwise that first frame reads as an error or as an account with nothing in it.
const isInitialOffersLoadPending = ref<boolean>(true);
const categoriesToShow = ref<string[]>([]);
const showRedeemedOnly = ref<boolean>(false);
const showExpiringSoonOnly = ref<boolean>(false);
//Soonest-to-lapse first by default: an offer a guest is about to lose is the one worth surfacing.
const sortMode = ref<OfferSortMode>('expiring');

const sortOptions: SelectOption[] = [
  { label: 'Expiring soonest', value: 'expiring', selected: true },
  { label: 'Newest', value: 'newest' },
  { label: 'Property', value: 'property' },
];
// const visibleOffers = computed(() => {
//   return (appStore.account.offers || []).filter((offer: OpcOffer) => {
//     return !categoriesToShow.value.length || categoriesToShow.value.includes(offer.OFFER_COPY_CATEGORY);
//   });
// });

//Keyed on the stored category, exactly as the buttons look it up. It used to key an offer with no category as
//'Uncategorized' while its button looked up '', so that button never showed a count.
const offerCountByCategory = computed(() => {
  const counts: Record<string, number> = {};
  (appStore.account.offers || []).forEach((offer) => {
    const category = offer.OFFER_COPY_CATEGORY || '';
    counts[category] = (counts[category] || 0) + 1;
  });
  return counts;
});

//In the order a guest reads them, by label, rather than by the stored codes.
const offerCategories = computed(() => {
  return (appStore.account.offers || [])
    .map((offer) => offer.OFFER_COPY_CATEGORY || '')
    .filter((category, index, self) => self.indexOf(category) === index)
    .sort((a, b) => getOfferCategoryLabel(a).localeCompare(getOfferCategoryLabel(b)));
});

const redeemedOfferCount = computed(() => {
  return (appStore.account.offers || []).filter((offer) => isOfferRedeemed(offer)).length;
});

const expiringSoonOfferCount = computed(() => {
  return (appStore.account.offers || []).filter((offer) => isOfferExpiringSoon(offer)).length;
});

//'Today' and 'soon' are the two urgencies the expiry badge calls out; the filter matches what the badge shows rather
//than inventing a second definition of urgent.
function isOfferExpiringSoon(offer: OpcOffer): boolean {
  return ['today', 'soon'].includes(getOfferExpiry(offer).urgency);
}

//The filters are separate axes, so an offer has to clear all of them to stay in the grid.
function isOfferVisible(offer: OpcOffer): boolean {
  if (showRedeemedOnly.value && !isOfferRedeemed(offer)) {
    return false;
  }

  if (showExpiringSoonOnly.value && !isOfferExpiringSoon(offer)) {
    return false;
  }

  return !categoriesToShow.value.length || categoriesToShow.value.includes(offer.OFFER_COPY_CATEGORY || '');
}

function getSortDateValue(offer: OpcOffer): number {
  if (!offer.sortDate) {
    return -Infinity;
  }

  const parsed = getDateFromEngage(offer.sortDate) ?? new Date(offer.sortDate);

  return isNaN(parsed.getTime()) ? -Infinity : parsed.getTime();
}

function compareOffers(a: OpcOffer, b: OpcOffer): number {
  if ('newest' === sortMode.value) {
    //Anything with no readable sortDate sorts last rather than to 1970.
    return getSortDateValue(b) - getSortDateValue(a);
  }

  if ('property' === sortMode.value) {
    const byProperty = getPropertyName(a.GUEST_PROPERTY_CODE, '').localeCompare(
      getPropertyName(b.GUEST_PROPERTY_CODE, ''),
    );

    return byProperty || a.OFFER_COPY_TITLE.localeCompare(b.OFFER_COPY_TITLE);
  }

  //'expiring': soonest end date first, whether or not the offer has been redeemed, with offers that have no readable end
  //date after them. getOfferExpiry() is not used: it reports no expiry for a redeemed offer, to keep the badge quiet.
  const endA = getEndDateValue(a);
  const endB = getEndDateValue(b);

  if (null === endA || null === endB) {
    if (endA === endB) {
      return getSortDateValue(b) - getSortDateValue(a);
    }

    return null === endA ? 1 : -1;
  }

  return endA - endB;
}

function getEndDateValue(offer: OpcOffer): number | null {
  const date = offer.OFFER_APPLICABLE_END_TS ? getDateFromEngage(offer.OFFER_APPLICABLE_END_TS) : null;

  return date && !isNaN(date.getTime()) ? date.getTime() : null;
}

function getOfferKey(offer: OpcOffer): string {
  return [
    offer.CASINO_OFFER_DOMAIN,
    offer.CASINO_OFFER_CODE,
    offer.CASINO_PATRON_ID,
    offer.CASINO_OFFER_INVITE_ID,
  ].join('::');
}

//The order is worked out when the sort changes or the set of offers does, and held until then. Sorting on every change
//moved an offer the moment it was redeemed or cancelled - a redeemed offer has no expiry, so 'expiring soonest' sent it
//to the bottom, under the patron's finger.
const sortedOfferKeys = ref<string[]>([]);

watch(
  [sortMode, () => (appStore.account.offers || []).map(getOfferKey).join('|')],
  () => {
    sortedOfferKeys.value = [...(appStore.account.offers || [])].sort(compareOffers).map(getOfferKey);
  },
  { immediate: true },
);

//Filters still apply live, so a redeemed offer leaves the 'Expiring Soon' filter, but nothing else changes places.
const visibleOffers = computed<OpcOffer[]>(() => {
  const rank = (offer: OpcOffer): number => {
    const index = sortedOfferKeys.value.indexOf(getOfferKey(offer));

    return -1 === index ? Infinity : index;
  };

  return (appStore.account.offers || []).filter(isOfferVisible).sort((a, b) => rank(a) - rank(b));
});

//getAccountOffers() returns early when a fetch started elsewhere is already running, so the store flag is what says
//whether offers are still on their way once the mounted call has handed back.
const isLoadingOffers = computed<boolean>(() => {
  return isInitialOffersLoadPending.value || appStore.isFetchOffersInProgress;
});

function filterByCategory(category: string | null) {
  if (category === null) {
    categoriesToShow.value = [];
    //'All Offers' is the reset for the whole bar, not just the categories.
    showRedeemedOnly.value = false;
    showExpiringSoonOnly.value = false;
  } else {
    categoriesToShow.value = [category];
    // if (!categoriesToShow.value.includes(category)) {
    //   categoriesToShow.value.push(category);
    // } else {
    //   categoriesToShow.value = categoriesToShow.value.filter(c => c !== category);
    // }
  }
}

//Offers change when they are redeemed, so five minutes. Past the server's cache, or the refresh could hand back what the
//patron already has.
useRevalidateOnActivate(() => getAccountOffers(true, true), 5 * 60 * 1000);

onMounted(async () => {
  try {
    await getAccountOffers(false);
  } finally {
    isInitialOffersLoadPending.value = false;
  }
});
</script>

<template>
  <peppermill-viewport :is-card-grid="isLoadingOffers || appStore.account.offers !== null">
    <template v-if="appStore.state.new_pin_required">
      <peppermill-content-block class="layout--content-wide">
        <h2>New PIN Required</h2>
        <p>You need to set/reset your PIN before we can show your offers.</p>
        <p>PINs can only be created or reset in person.</p>
        <p>You can still call 800-217-0049 to talk with a representative about your offers.</p>
      </peppermill-content-block>
    </template>
    <peppermill-content-block v-if="isLoadingOffers && !appStore.account.offers?.length">
      <p>Loading your offers...</p>
    </peppermill-content-block>
    <peppermill-content-block v-else-if="!isLoadingOffers && appStore.account.offers === null">
      <p>Error Loading Offers</p>
    </peppermill-content-block>
    <peppermill-content-block v-else-if="!isLoadingOffers && !appStore.account.offers?.length">
      <p>No Offers Available</p>
    </peppermill-content-block>

    <template v-else>
      <div class="view_offer--back_btn_wrapper">
        <router-link to="/" class="view_offer--back_btn"
          ><span class="material-icons">arrow_back</span>Back to Home</router-link
        >
      </div>

      <div class="offers--filter" v-if="offerCategories.length > 1 || redeemedOfferCount || expiringSoonOfferCount">
        <material-button
          label="All Offers"
          @click.prevent="filterByCategory(null)"
          type="button"
          :mod="!categoriesToShow.length && !showRedeemedOnly && !showExpiringSoonOnly ? 'raised' : 'text'"
          :data-offer-count="appStore.account.offers?.length || undefined"
        ></material-button>
        <material-button
          v-for="category in offerCategories"
          :key="category"
          :label="getOfferCategoryLabel(category)"
          @click.prevent="filterByCategory(category)"
          type="button"
          :mod="categoriesToShow.includes(category) ? 'raised' : 'text'"
          :data-offer-count="offerCountByCategory[category] || undefined"
        ></material-button>
        <material-button
          v-if="expiringSoonOfferCount"
          label="Expiring Soon"
          @click.prevent="showExpiringSoonOnly = !showExpiringSoonOnly"
          type="button"
          :mod="showExpiringSoonOnly ? 'raised' : 'text'"
          :data-offer-count="expiringSoonOfferCount"
        ></material-button>
        <material-button
          v-if="redeemedOfferCount"
          label="Redeemed"
          @click.prevent="showRedeemedOnly = !showRedeemedOnly"
          type="button"
          :mod="showRedeemedOnly ? 'raised' : 'text'"
          :data-offer-count="redeemedOfferCount"
        ></material-button>
      </div>

      <div class="offers--sort" v-if="(appStore.account.offers?.length || 0) > 1">
        <material-form-select
          name="offer_sort"
          label="Sort by"
          :options="sortOptions"
          @update:value="sortMode = $event"
        ></material-form-select>
      </div>

      <peppermill-content-block v-if="!visibleOffers.length">
        <p>No offers match the filters you have selected.</p>
      </peppermill-content-block>

      <transition-group name="list">
        <offer
          v-for="offer in visibleOffers"
          :key="getOfferKey(offer)"
          :offer="offer"
        ></offer>
      </transition-group>
    </template>
  </peppermill-viewport>
</template>

<style scoped>
.view_offer--back_btn {
  display: flex;
  margin-bottom: 0.25em;
  text-decoration: none;
  align-items: center;
}

.view_offer--back_btn_wrapper {
  max-width: 648px;
  margin: 0 auto;
  grid-column: 1 / -1;
}

.offers--filter {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.offers--sort {
  grid-column: 1 / -1;
  max-width: 16em;
}

/*.offers--filter .button--container[data-offer-count]::after {
  content: attr(data-offer-count);
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  border-radius: 50%;
  background-color: #FFF;
  box-shadow: 2px 2px 8px 0 rgb(0 0 0 / 35%);
  width: 1.5em;
  height: 1.5em;
  line-height: 1.5em;
  vertical-align: middle;
  z-index: 1;
}

.offers--filter .button--container-raise d[data-offer-count]::after {
  background-color: rgb(var(--primary-color));
}*/

/*.list-move,*/
.list-enter-active,
.list-leave-active {
  transition:
    opacity 150ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

@media (prefers-reduced-motion: reduce) {
  .list-move,
  .list-enter-active,
  .list-leave-active {
    transition: none;
  }
}
</style>
